import { buildComparisonTableConfig } from '@/config/comparisonTableFields'
import { resolveBuildingPart } from '@/services/buildingPartResolver'
import { pruneStaleComparables } from '@/services/comparisonTableReconcile'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEsgStore } from '@/stores/useEsgStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import { useLandBoardStore } from '@/stores/useLandBoardStore'
import { useTransactionStore } from '@/stores/transaction'

/**
 * Hydrate the Pinia stores backing the QV result view + short-report export
 * from a persisted prediction payload. Centralises the cascade so the live
 * `QuickValuationResult.vue` page and the `QuickValuationHistory.vue` download
 * dialog produce identical store state — and therefore identical
 * `transactionStore.comparisonTableExport` rows/cols when the export request
 * is built.
 *
 * Without this, the history dialog's `collectTransactions()` short-circuits
 * (cols/rows empty) and the server falls back to the simple 3-row template
 * (`fillSimilarPropertiesTable`) instead of the full Expert kohandustabel
 * (`fillFullComparisonTable`). RED-212.
 */
export function useQuickValuationHydration() {
  const detailStore = useDetailStore()
  const landBoardStore = useLandBoardStore()
  const evaluationStore = useEvaluationStore()
  const transactionStore = useTransactionStore()
  const esgStore = useEsgStore()

  /**
   * Run the hydration cascade. Mirrors the Detail flow's entry point so the
   * comparison-table master row + comparable rows are populated identically
   * to the live page.
   *
   * @param {object} ctx
   * @param {object} ctx.evaluation       Persisted evaluation row (id, cadaster_id, construction, interior, technical, …).
   * @param {object} [ctx.ehrData]        EHR snapshot (livingRoom, general, ehitiseAndmed, …).
   * @param {object} [ctx.landBoard]      LandBoardService payload (bbox, …).
   * @param {object} [ctx.prediction]     Persisted ML prediction bundle.
   * @param {string|null} [ctx.buildingPartId] Selected apartment's `ehitise-osa-number`.
   * @param {{ skipEsgFetch?: boolean }} [opts]
   */
  const hydrate = async (
    { evaluation, ehrData = {}, landBoard = null, prediction = {}, buildingPartId = null } = {},
    { skipEsgFetch = false } = {},
  ) => {
    // Drop any stale subject-property ("master") column left in the shared
    // (singleton) transaction store by a previous module. setPropertyData below
    // rebuilds it from this run's EHR; clearing first stops a previous address's
    // master row from surviving when this QV has no EHR to overwrite it. ESG +
    // landBoard are left untouched so the skipEsgFetch reuse below still holds.
    transactionStore.masterData = null
    transactionStore.clearMasterOverrides()

    const cadasterId = evaluation?.cadaster_id ?? ''

    // Resolve the subject apartment by its globally-unique adsOid (the shape
    // building_part_id is persisted in — QuickValuation.vue:671). The old code
    // matched ONLY `ehitise-osa-number` and fell back to `livingRoom[0]`, so an
    // adsOid id (e.g. "ER01237671") matched nothing and silently picked the
    // first apartment — the downloaded report then showed a foreign flat's
    // master column while the web (which matches adsOid) showed the right one
    // (eval 6139: Sõle tn 15-27). Shared with QuickValuationResult.vue's
    // `selectedBuildingPart`; no first-element fallback.
    const buildingPart = resolveBuildingPart(ehrData?.livingRoom, buildingPartId)

    // CRITICAL: set buildingPart and pre-fill the wizard's manual edits BEFORE
    // setPropertyData runs. setLandBoardData inside setPropertyData fires
    // `storeMasterData()` synchronously, and useTransactionComparisonMaster-
    // Details reads from these refs to build the kohandustabel's master column
    // (ehitise_seisukord, hooneosa_seisukord, rodu, panipaik, koogimoobel,
    // hooneosa_pind, hooneosa_tubade_arv, korteri_pind, korruste_arv, …).
    // setEhrData uses isEmpty() guards so wizard values pre-loaded here are
    // preserved (EHR only fills empty slots).
    if (buildingPart) {
      evaluationStore.buildingPart = buildingPart
    }
    if (evaluation?.construction && typeof evaluation.construction === 'object') {
      Object.assign(detailStore.ehrConstruction, evaluation.construction)
    }
    if (evaluation?.interior && typeof evaluation.interior === 'object') {
      Object.assign(detailStore.interior, evaluation.interior)
    }
    if (evaluation?.technical && typeof evaluation.technical === 'object') {
      Object.assign(detailStore.ehrTechnical, evaluation.technical)
    }
    if (prediction) {
      evaluationStore.prediction = prediction
    }

    // Cascade through the Detail flow's entry point — this covers:
    //   - setEhrData: populates ehrId, ehrGeometry, ehrGeneral/Technical/
    //     Construction/Communal, ehrLivingRoom/NotLivingRoom, ehrAds
    //   - setLandBoardData: populates landBoardStore + triggers storeMasterData
    //     (which reads the EHR + landBoard + wizard fields back out to compose
    //     masterData for the comparison table)
    //   - fetchAllEsgData: populates esgStore.esgData[id].user.description
    if (ehrData && (ehrData.general || ehrData.ehitiseAndmed)) {
      const sanitizedLandBoard = skipEsgFetch && landBoard
        ? { ...landBoard, bbox: null }
        : landBoard
      try {
        await detailStore.setPropertyData(cadasterId, {
          xRoadEhr: ehrData,
          landBoard: sanitizedLandBoard,
        })
      } catch (err) {
        console.warn('[useQuickValuationHydration] setPropertyData failed', err)
      }
    } else {
      // No EHR data yet — just mirror landBoard so the hidden maps don't draw blank.
      landBoardStore.landBoard = landBoard ?? {}
    }

    // Default kohandustabel field config (same builder the Detail flow uses).
    // Set BEFORE seeding rows so reactive computeds in transactionStore see the
    // right field set when rows arrive.
    transactionStore.markedTableConf = buildComparisonTableConfig({
      hasBuildingPart: !!buildingPart,
    })
    // RED-296: hidden groups are Detail-flow state (persisted per evaluation);
    // the shared store must not leak them into a QV kohandustabel.
    transactionStore.hiddenComparisonGroups = []

    // Seed the transactionStore via the Expert flow's loader path
    // (`evaluationStore.initFromSavedPrediction`). It hits
    // `/api/transactions/{id}/details` + `/buildings` for each comparable,
    // runs `composeData` to produce the same per-cell field shape the Expert
    // kohandustabel renders (tehingusumma, korteri_pind, kandekonstruktsioon,
    // ehitise_seisukord, hooneosa_*, tehnovorgud, kute, …), then layers in
    // `prediction.grouped_differences[id]` + `_raw` mirror of
    // `grouped_differences_unrounded[id]`.
    const similarProperties = prediction?.similar_properties ?? []

    // Drop comparables left over from a prior module/address before seeding this
    // run's prediction. The shared transaction store is a singleton; without
    // this, an Evaluation of the SAME address opened just before leaks its rows
    // into the QV comparison table — the web showed 3 but the export read 6 at
    // the COOP demo (2026-06-15). Matching ids are kept to avoid a redundant
    // refetch when deferred props re-trigger hydrate. The leak-guard in
    // QuickValuationResult.vue only covered QV→QV, not Hindamine→QV / first load.
    transactionStore.selectionTableRows = pruneStaleComparables(
      transactionStore.selectionTableRows,
      similarProperties.map(p => p.property_id),
    )

    if (Array.isArray(similarProperties) && similarProperties.length > 0) {
      try {
        await evaluationStore.initFromSavedPrediction(prediction)
        // initFromSavedPrediction defers is_comparable to composeData (truthy
        // when a transaction is loaded). Force the flag for safety so
        // `comparableTransactions` always picks them up.
        transactionStore.selectionTableRows.forEach(row => {
          row.is_comparable = true
          row.is_marked = true
        })
      } catch (err) {
        console.warn(
          '[useQuickValuationHydration] failed to hydrate comparable transactions',
          err,
        )
      }
    }
  }

  /**
   * Reset the stores back to a neutral state. Use when navigating away from
   * the QV result page, or after closing the history download dialog, so the
   * next hydration starts clean (no row-A leakage into row-B).
   */
  const reset = () => {
    detailStore.ehrId = null
    detailStore.ehrGeometry = null
    landBoardStore.landBoard = {}
    transactionStore.selectionTableRows = []
    transactionStore.markedTransactions = []
    transactionStore.masterData = null
    transactionStore.clearMasterOverrides()
    transactionStore.markedTableConf = {}
    transactionStore.hiddenComparisonGroups = []
    evaluationStore.prediction = {}
    evaluationStore.buildingPart = null

    // RED-214: ESG state must be cleared between QV runs. Without this, the
    // exported Mall for evaluation B can carry evaluation A's auto-generated
    // descriptions (e.g. "no noise" when the new address has noise) because
    // `fetchAllEsgData` only re-populates `esgData` on a fresh bbox fetch and
    // `collectEsgData()` in ShortReportDialog reads the per-key user.description
    // straight from the store at click time.
    //
    // RED-355: the store's own reset, so nothing is missed — the korteriühistu
    // name included: the annex C table prints it (row „Hinnang kinnisvara
    // juhtimise kohta“, source äriregister), and a land parcel B never looks
    // one up, so A's would stay. A lookup A still has in flight is dropped too.
    // The summary is cleared on purpose: the annex C table reads
    // `esgStore.quickValuationAnnexCRows`, which falls back to the whole form
    // („info puudub“ per factor) while it is empty, and every hydrate that
    // builds one starts from here.
    esgStore.resetForEvaluation()
  }

  return { hydrate, reset }
}
